import { useState } from 'react'

import { omit } from 'lodash'
import { DateTime } from 'luxon'

import { SimpleTable, TableSkeleton, Alert, SeverityPill, AssetDetailModal } from '@/components/ui'
import { useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { buildAlertDescription } from '@/insights/common/alertDescription'
import alertLocationDrilldown from '@/insights/common/alertLocationDrilldown'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import LiveDuration from '@/insights/components/LiveDuration'
import { getSeverityLabel } from '@/insights/components/SeverityDropdown'
import SingleValueDisplay from '@/insights/components/SingleValueDisplay'
import {
  ApiService,
  type InsightDefinition,
  InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
  AlertData,
  InsightConfigurationType,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'

export interface AlertsDetailParams extends InsightParametersType {
  companyId?: number
  status?: string
  severity?: string[]
  geofenceIds?: string
  geofenceCategoryIds?: string
  alertConfigIds?: string
  date?: string
  from?: string
  to?: string
  isDrilldown?: boolean
  displayFormat: string
}

const AlertsActive: InsightDefinition<AlertsDetailParams, AlertData[] | number> = {
  active: true,
  slug: 'alert/alerts-active',
  category: 'alert',
  name: 'Active Alerts',
  description: 'List active alerts, optionally filtered by alert and severity',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'severity') return { ...parameters, alertConfigIds: undefined }
      if (field === 'geofenceIds') {
        return { ...parameters, geofenceCategoryIds: undefined }
      }
      if (field === 'geofenceCategoryIds') {
        return { ...parameters, geofenceIds: undefined }
      }
      if (field === 'alertConfigIds') return { ...parameters, severity: undefined }
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, activeSince: undefined }
      }
      return parameters
    },
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'note', control: 'Label', label: 'Choose either Severity or Alert Name' },
      { id: 'alertConfigIds', control: 'AlertNameDropdown', required: false },
      { id: 'severity', control: 'SeverityDropdown' },
      { id: 'note', control: 'Label', label: 'Choose either Geofence or Geofence Category' },
      { id: 'geofenceIds', control: 'GeofenceDropdown', required: false },
      { id: 'geofenceCategoryIds', control: 'GeofenceCategoryDropdown', required: false },
      {
        id: 'date',
        label: 'Active Since',
        control: 'CustomSingleSelect',
        options: DateOptions,
      },
      {
        id: 'customDate',
        label: 'Raised Time',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
      {
        id: 'displayFormat',
        control: 'CustomSingleSelect',
        options: [
          { label: 'Table', value: 'table', isDefault: true },
          { label: 'Single Value Chart', value: 'single' },
        ],
      },
    ],

    isValid: (configuration: AlertsDetailParams) => {
      const hasSeverity = configuration.severity && configuration.severity.length > 0
      const hasAlert = configuration.alertConfigIds && configuration.alertConfigIds.length > 0

      if (configuration.date === 'custom' && !configuration.from && !configuration.to) {
        return 'Enter the custom date for Raised Time'
      }
      if (configuration.date === 'custom') {
        if (configuration.from && configuration.to) {
          const fromDate = new Date(configuration.from)
          const toDate = new Date(configuration.to)
          if (fromDate > toDate) {
            return ' Start date must be before end date'
          }
        }
        const dateValidation = validateDateRangeWithin13Months(configuration.from, configuration.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }
      if (configuration?.companyId) {
        if (hasSeverity !== hasAlert) {
          return true
        }
        if (!hasSeverity && !hasAlert) {
          return 'Alert Name or Severity is required'
        }
        return 'Choose either Alert Name or Severity'
      }
      return 'Company is required.'
    },
  },
  csv: async (api: ApiService, parameters: AlertsDetailParams, title: string) => {
    const path = '/insights/alerts/alerts-active'
    await downloadCsv(api, path, parameters, title)
  },
  load: async (api: ApiService, parameters: AlertsDetailParams) => {
    let skipTake = '&skip=0&take=1000'
    if (parameters.displayFormat === 'single') {
      skipTake = '&skip=0&take=1'
    }
    const params = { ...parameters }
    const path = `/insights/alerts/alerts-active?${buildQuery(params)}${skipTake}`
    const response = await api.get<AlertData[]>({
      path,
      origin: 'AlertsActive#loader',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    if (parameters.displayFormat === 'single') {
      return response.count || 0
    }
    return response.data
  },

  Display: ({ status, data, configuration }) => {
    const { companies } = useCompany()
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const units = (useDashboard().configuration.units as 'Imperial' | 'Metric') ?? 'Metric'

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      // This could be handled by the framework by just returning the
      // same status. But we should make the skeleton match the final
      // table result if possible.
      return <TableSkeleton columns={6} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No alarms found for severity critical".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }

    const parameters = configuration.parameters as AlertsDetailParams

    if (parameters.displayFormat === 'single') {
      const company = companies.find((c) => c.id === parameters.companyId)
      const companyName = company ? company.name : ''
      const ddConfig: InsightConfigurationType = {
        ...configuration,
        slug: 'alert/alerts-active',
        title: `${companyName} ${configuration.title}: Details`,
        parameters: {
          ...parameters,
          displayFormat: 'table',
          status: 'active',
          isDrilldown: true,
        },
      }

      return <SingleValueDisplay value={data as number} width={configuration.width} drilldownConfiguration={ddConfig} />
    }

    const alerts = data as AlertData[]
    if (!alerts?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }

    const uniqueCompanies = new Set(alerts.map((alert) => alert.company.id))
    const shouldShowCompany = (parameters.isDrilldown && uniqueCompanies.size > 1) || uniqueCompanies.size > 1

    const assetInfo = parameters.assetIds && alerts.length > 0 ? `${alerts[0].company.name} ` : null

    const body = alerts
      ?.sort((a, b) => {
        const t = b.raisedTime.getTime() - a.raisedTime.getTime()
        if (t === 0) {
          return a.asset.name.localeCompare(b.asset.name)
        }
        return t
      })

      .map((alert) => ({
        ...(shouldShowCompany && {
          Company: <span>{alert.company.name}</span>,
        }),

        Asset: (
          <button
            onClick={() => setSelectedAsset(alert.asset.id)}
            className="text-left block text-blue-500 cursor-pointer"
          >
            {alert.asset.name}
          </button>
        ),
        Alert: <span className="block text-left">{alert.alertName}</span>,
        Description: (
          <span className="block text-left">
            {buildAlertDescription({
              behavior: alert.behavior,
              alertCustomDetails: alert.alertCustomDetails,
              units,
            })}
          </span>
        ),
        Raised: {
          id: `raised-${alert.id}`,
          date: alert.raisedTime,
          display: DateTime.fromJSDate(alert.raisedTime).toFormat('yyyy-MM-dd HH:mm'),
        },
        Duration: {
          value: (alert.clearedTime || new Date()).getTime() - alert.raisedTime.getTime(),
          display: (
            <LiveDuration
              raisedTime={alert.raisedTime}
              clearedTime={alert.clearedTime}
              className="block text-left min-w-fit"
            />
          ),
        },
        Severity: {
          value: alert.severity,
          display: <SeverityPill label={getSeverityLabel(String(alert.severity))} severityCode={alert.severity} />,
        },

        Geofence: alertLocationDrilldown(alert, configuration, 'raisedGeofence', parameters.companyId),

        Address: alertLocationDrilldown(alert, configuration, 'raised', parameters.companyId),
      }))

    let body2
    if (configuration.parameters.status === 'raised') {
      body2 = body.map((row) => omit(row, 'Cleared'))
    }

    const dataItem = (body2 || body)?.[0]

    const headers = dataItem
      ? Object.keys(dataItem)
      : Array(3)
          .fill(null)
          .map((_, index) => `placeholder-${index}`)

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        {assetInfo && <div className="px-2 py-1 text-lg font-medium">{assetInfo}</div>}
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body2 || body} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return 'Error'
    }
    const fields: Record<string, string> = {}
    const params = configuration.parameters as AlertsDetailParams

    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.severity?.length) {
      const p = typeof params.severity === 'string' ? [params.severity] : params.severity
      const labels = p
        .map((s) => getSeverityLabel(s))
        .filter((l) => !!l)
        .join(', ')
      fields['Severity'] = labels
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ data, status, configuration }) => {
    if (configuration.parameters.displayFormat === 'single') {
      return ''
    }
    if (status.code === InsightStatusCode.LOADING && data === undefined) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = (data as AlertData[])?.length

    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default AlertsActive
